import { Box, Button, Flex, Text, Textarea } from "@chakra-ui/react";
import React from "react";

const MAX_FEEDBACK_LENGTH = 2000;

const FEEDBACK_PLACEHOLDER = [
  "e.g. move Tuesday to start at 10:00; drop Sundays and put those hours on Friday;",
  "make the weeks more even",
].join("\n");

interface Props {
  /** Refinements this conversation still allows. */
  remainingTurns: number;
  /** Generations left for this patient today — a refinement spends one. */
  remainingQuota: number | null;
  isPending: boolean;
  isDisabled: boolean;
  onSubmit: (feedback: string) => void;
}

/**
 * "Not quite — change this" for the suggestion currently on screen.
 *
 * Sits directly under the apply bar: both are things to do with this result, and
 * a real result runs to several tables inside a scrolling body, so anything below
 * them is off screen every time.
 *
 * Renders nothing once the conversation has been refined as far as it may be —
 * the server refuses past that point, and an input that cannot be sent is worse
 * than no input.
 */
export default function AiWeeklyTemplateFeedbackBox(props: Props) {
  const [feedback, setFeedback] = React.useState("");

  if (props.remainingTurns <= 0) {
    return null;
  }

  const trimmed = feedback.trim();
  const canSubmit = trimmed !== "" && !props.isPending && !props.isDisabled;

  return (
    <Box borderColor="gray.200" borderRadius="md" borderWidth="1px" p={3}>
      <Text fontWeight="semibold" pb={1}>
        Not quite right? Tell the assistant what to change
      </Text>
      <Text color="gray.600" fontSize="sm" pb={2}>
        It keeps everything you do not mention and re-checks every limit, so a revision is verified
        exactly like the first suggestion. The version you apply is the one you have open.
      </Text>
      <Textarea
        isDisabled={props.isPending || props.isDisabled}
        maxLength={MAX_FEEDBACK_LENGTH}
        placeholder={FEEDBACK_PLACEHOLDER}
        rows={3}
        value={feedback}
        onChange={(event) => setFeedback(event.target.value)}
      />
      <Flex alignItems="center" justifyContent="space-between" pt={2}>
        <Text color="gray.500" fontSize="xs">
          {props.remainingTurns} revision{props.remainingTurns === 1 ? "" : "s"} left for this
          suggestion
          {props.remainingQuota === null
            ? ""
            : ` · uses 1 of the patient's ${props.remainingQuota} generations left today`}
        </Text>
        <Button
          colorScheme="blue"
          isDisabled={!canSubmit}
          isLoading={props.isPending}
          loadingText="Revising…"
          size="sm"
          variant="outline"
          onClick={() => {
            props.onSubmit(trimmed);
            setFeedback("");
          }}
        >
          Send feedback
        </Button>
      </Flex>
    </Box>
  );
}
